import React from "react";
import PropTypes from "prop-types";
import { MapPin, X } from "lucide-react";

import { UIAvatar, UIChip } from "../../../ui";
import { getLocalizedRoleName } from "../../../../utils/Helpers";
import { getRoleBucket, getWorkerRoleBucket, isWorkerOffline } from "./heatmapHelpers";

const EventHeatMapTooltip = ({ t, lang, cluster, position, placement, isClientViewer, onClose }) => {
  const chatClass =
    placement === "bottom" ? "event-heatmap__tooltip--chat-below" : "event-heatmap__tooltip--chat";

  if (cluster.members.length === 1) {
    const member = cluster.members[0];
    return (
      <div
        className={`event-heatmap__tooltip ${chatClass} event-heatmap__tooltip--single`}
        style={position || undefined}
      >
        <div className="event-heatmap__tooltip-single-header">
          <div className="event-heatmap__tooltip-single-identity">
            <UIAvatar
              size="sm"
              image={member.user?.image}
              name={member.user?.name}
              className={`event-heatmap__tooltip-avatar event-heatmap__tooltip-avatar--${getWorkerRoleBucket(
                member,
              )}`}
            />
            {!isClientViewer && (
              <span className="event-heatmap__tooltip-single-name">{member.user?.name}</span>
            )}
          </div>
          <button
            type="button"
            className="event-heatmap__tooltip-close"
            aria-label={t.close}
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
        {!isClientViewer && (
          <div className="event-heatmap__tooltip-chip-row">
            {isWorkerOffline(member) && (
              <UIChip
                size="small"
                shape="pill"
                label={t.offline}
                className="event-heatmap__tooltip-role-chip event-heatmap__tooltip-role-chip--offline"
              />
            )}
            <UIChip
              size="small"
              shape="pill"
              label={getLocalizedRoleName(member.role, lang)}
              className={`event-heatmap__tooltip-role-chip event-heatmap__tooltip-role-chip--${getRoleBucket(
                member,
              )}`}
            />
          </div>
        )}
        <div className="event-heatmap__tooltip-divider" />
        <div className="event-heatmap__tooltip-zone-row">
          <MapPin size={16} />
          {member.zone?.name}
        </div>
      </div>
    );
  }

  return (
    <div className={`event-heatmap__tooltip ${chatClass}`} style={position || undefined}>
      <div className="event-heatmap__tooltip-header">
        <span className="event-heatmap__tooltip-title">
          {t.staffMembersGrouped.replace("{count}", cluster.members.length)}
        </span>
        <button
          type="button"
          className="event-heatmap__tooltip-close"
          aria-label={t.close}
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      <span className="event-heatmap__tooltip-subtitle">{t.groupedHint}</span>
      <div className="event-heatmap__tooltip-list">
        {cluster.members.map((member) => (
          <div className="event-heatmap__tooltip-row" key={member.id}>
            <UIAvatar
              size="sm"
              image={member.user?.image}
              name={member.user?.name}
              className={`event-heatmap__tooltip-avatar event-heatmap__tooltip-avatar--${getWorkerRoleBucket(
                member,
              )}`}
            />
            {!isClientViewer && (
              <div className="event-heatmap__tooltip-info">
                <span className="event-heatmap__tooltip-name">{member.user?.name}</span>
                <span
                  className={`event-heatmap__tooltip-role event-heatmap__tooltip-role--${getWorkerRoleBucket(
                    member,
                  )}`}
                >
                  {isWorkerOffline(member) ? t.offline : getLocalizedRoleName(member.role, lang)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

EventHeatMapTooltip.propTypes = {
  t: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  cluster: PropTypes.shape({
    members: PropTypes.array.isRequired,
  }).isRequired,
  position: PropTypes.shape({ left: PropTypes.number, top: PropTypes.number }),
  placement: PropTypes.oneOf(["top", "bottom"]),
  isClientViewer: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

EventHeatMapTooltip.defaultProps = {
  position: null,
  placement: "top",
  isClientViewer: false,
};

export default EventHeatMapTooltip;
